'use client';

import { useCallback, useEffect, useEffectEvent } from 'react';

import { usePathname } from 'next/navigation';

import { analytics } from '@kit/analytics';
import {
  AppEvent,
  AppEventType,
  ConsumerProvidedEventTypes,
  useAppEvents,
} from '@kit/shared/events';
import { isBrowser } from '@kit/shared/utils';

import type { ChatEventTypes } from '~/(chat)/_lib/chat-events';
import { hashFileName } from '~/(chat)/chat/_lib/hash-file-name';

type AnalyticsMapping<
  T extends ConsumerProvidedEventTypes = NonNullable<unknown>,
> = {
  [K in AppEventType<T>]?: (event: AppEvent<T, K>) => unknown;
};

/**
 * Hook to subscribe to app events and map them to analytics actions
 * @param mapping
 */
function useAnalyticsMapping<T extends ConsumerProvidedEventTypes>(
  mapping: AnalyticsMapping<T>,
) {
  const appEvents = useAppEvents<T>();

  const subscribeToAppEvent = useEffectEvent(
    (
      eventType: AppEventType<T>,
      handler: (event: AppEvent<T, AppEventType<T>>) => unknown,
    ) => {
      appEvents.on(eventType, handler);
    },
  );

  const unsubscribeFromAppEvent = useEffectEvent(
    (
      eventType: AppEventType<T>,
      handler: (event: AppEvent<T, AppEventType<T>>) => unknown,
    ) => {
      appEvents.off(eventType, handler);
    },
  );

  useEffect(() => {
    const subscriptions = Object.entries(mapping).map(
      ([eventType, handler]) => {
        subscribeToAppEvent(eventType as AppEventType<T>, handler);

        return () =>
          unsubscribeFromAppEvent(eventType as AppEventType<T>, handler);
      },
    );

    return () => {
      subscriptions.forEach((unsubscribe) => unsubscribe());
    };
  }, [mapping]);
}

/**
 * Define a mapping of app events to analytics actions
 * Add new mappings here to track new events in the analytics service from app events
 */
const analyticsMapping: AnalyticsMapping<ChatEventTypes> = {
  'user.signedIn': (event) => {
    const { userId, ...traits } = event.payload;

    if (userId) {
      return analytics.identify(userId, traits);
    }
  },
  'user.signedUp': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'checkout.started': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'user.updated': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  // Chat events
  'confidential-mode-activated': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'confidential-mode-closed': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'file-uploaded': async (event) => {
    const payload = { ...event.payload };

    if (payload.isPrivateMode === 'true') {
      payload.fileName = await hashFileName(payload.fileName);
    }

    return analytics.trackEvent(event.type, payload);
  },
  'image-generated': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'theme-changed': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'loopback-config-updated': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'confidential-data-exported': (event) => {
    return analytics.trackEvent(event.type, {
      chatCount: String(event.payload.chatCount),
      blobCount: String(event.payload.blobCount),
    });
  },
  'confidential-data-imported': (event) => {
    return analytics.trackEvent(event.type, {
      chatCount: String(event.payload.chatCount),
      blobCount: String(event.payload.blobCount),
    });
  },
  'tts-generated': (event) => {
    return analytics.trackEvent(event.type, {
      id: event.payload.id,
      model: event.payload.model,
      textLength: String(event.payload.textLength),
    });
  },
  'mode-selected': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'banner-impression': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'banner-cta-clicked': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'banner-dismissed': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'news-popup-impression': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'news-popup-dismissed': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'news-popup-link-clicked': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'cm-tool-enabled': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'cm-tool-approval-requested': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'cm-tool-approved': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'cm-tool-denied': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'conversation-shared': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'conversation-forked': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'conversation-unshared': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'conversation-exported': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'project-shared': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'project-unshared': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'at-mention-selected': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'voice-mode-started': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'voice-mode-ended': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'voice-mode-limit-reached': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'voice-mode-bargein': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'compaction-kb-created': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'compaction-kb-banner-dismissed': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  'cm-sdk-status-changed': (event) => {
    return analytics.trackEvent(event.type, event.payload);
  },
  // NOTE: skill analytics (skill-loaded / skill-created / skill-promoted) are
  // tracked via direct analytics.trackEvent calls at their source — the chat
  // data-stream handler (load/create) and the admin promote action — rather
  // than through the app-event bus, so they have no entry here.
};

function AnalyticsProviderBrowser(props: React.PropsWithChildren) {
  // Subscribe to app events and map them to analytics actions
  useAnalyticsMapping<ChatEventTypes>(analyticsMapping);

  // Report page views to the analytics service
  useReportPageView(useCallback((url) => analytics.trackPageView(url), []));

  // Render children
  return props.children;
}

/**
 * Provider for the analytics service
 */
export function AnalyticsProvider(props: React.PropsWithChildren) {
  if (!isBrowser()) {
    return props.children;
  }

  return <AnalyticsProviderBrowser>{props.children}</AnalyticsProviderBrowser>;
}

/**
 * Hook to report page views to the analytics service
 * @param reportAnalyticsFn
 */
function useReportPageView(reportAnalyticsFn: (url: string) => unknown) {
  const pathname = usePathname();

  const callAnalyticsOnPathChange = useEffectEvent(() => {
    const search = typeof window !== 'undefined' ? window.location.search : '';
    const url = [pathname, search.replace('?', '')].filter(Boolean).join('?');

    return reportAnalyticsFn(url);
  });

  useEffect(() => {
    callAnalyticsOnPathChange();
    // call whenever the pathname changes
  }, [pathname]);
}
